import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_API_URL } from "../../../utility/api";

export const appointmentsApi = createApi({
  reducerPath: "appointment",
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_API_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // ── Create ──
    createAppointment: builder.mutation({
      query: (data) => ({ url: "users/appointments", method: "POST", body: data }),
    }),

    // ── Get All (with filters, tabs, pagination) ──
    getAppointments: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        status = "",
        payment_status = "",
        tab = "",
        doctor_id = "",
        service_id = "",
        date_from = "",
        date_to = "",
      } = {}) => {
        const params = new URLSearchParams({ page, limit });
        if (search) params.append("search", search);
        if (status) params.append("status", status);
        if (payment_status) params.append("payment_status", payment_status);
        if (tab) params.append("tab", tab);
        if (doctor_id) params.append("doctor_id", doctor_id);
        if (service_id) params.append("service_id", service_id);
        if (date_from) params.append("date_from", date_from);
        if (date_to) params.append("date_to", date_to);
        return `users/appointments?${params.toString()}`;
      },
      providesTags: ["Appointments"],
    }),

    // ── Get Single ──
    getAppointmentDetails: builder.query({
      query: (id) => `users/appointments/${id}`,
    }),

    // ── Update ──
    updateAppointmentDetails: builder.mutation({
      query: ({ data, id }) => ({
        url: `users/appointments/${id}`,
        method: "PUT",
        body: data,
      }),
    }),

    // ── Delete ──
    deleteAppointmentsDetails: builder.mutation({
      query: (id) => ({ url: `users/appointments/${id}`, method: "DELETE" }),
    }),

    // ── Start Consultation ──
    startConsultation: builder.mutation({
      query: (id) => ({
        url: `users/appointments/${id}/start-consultation`,
        method: "PATCH",
      }),
    }),

    // ── Mark Completed ──
    markCompleted: builder.mutation({
      query: (id) => ({
        url: `users/appointments/${id}/mark-completed`,
        method: "PATCH",
      }),
    }),

    // ── Cancel ──
    cancelAppointment: builder.mutation({
      query: ({ id, data }) => ({
        url: `users/appointments/${id}/cancel`,
        method: "PATCH",
        body: data,
      }),
    }),

    // ── Update Payment Status ──
    updatePaymentStatus: builder.mutation({
      query: ({ id, data }) => ({
        url: `users/appointments/${id}/payment-status`,
        method: "PATCH",
        body: data,
      }),
    }),

    // ── Reschedule Request ──
    rescheduleRequest: builder.mutation({
      query: ({ id, data }) => ({
        url: `users/appointments/${id}/reschedule-request`,
        method: "POST",
        body: data,
      }),
    }),

    // ── Approve Reschedule ──
    approveReschedule: builder.mutation({
      query: (id) => ({
        url: `users/appointments/${id}/approve-reschedule`,
        method: "PATCH",
      }),
    }),

    // ── Add Note ──
    addNote: builder.mutation({
      query: ({ id, data }) => ({
        url: `users/appointments/${id}/notes`,
        method: "POST",
        body: data,
      }),
    }),

    // ── Get Available Slots ──
    getAvailableSlots: builder.query({
      query: ({ doctor_id, date }) =>
        `users/appointments/slots?doctor_id=${doctor_id}&date=${date}`,
    }),
  }),
});

export const {
  useCreateAppointmentMutation,
  useGetAppointmentsQuery,
  useGetAppointmentDetailsQuery,
  useUpdateAppointmentDetailsMutation,
  useDeleteAppointmentsDetailsMutation,
  useStartConsultationMutation,
  useMarkCompletedMutation,
  useCancelAppointmentMutation,
  useUpdatePaymentStatusMutation,
  useRescheduleRequestMutation,
  useApproveRescheduleMutation,
  useAddNoteMutation,
  useGetAvailableSlotsQuery,
} = appointmentsApi;